import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminContext = createContext();

export const useAdmin = () => useContext(AdminContext);

export const AdminProvider = ({ children }) => {
  const [token, setToken] = useState(() => localStorage.getItem("adminToken"));
  const [admin, setAdmin] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const getAuthHeaders = useCallback(() => ({
    Authorization: `Bearer ${token}`,
  }), [token]);

  // Check authentication
  const checkAuth = useCallback(async () => {
    if (!token) {
      setIsAuthenticated(false);
      setAdmin(null);
      return;
    }
    try {
      const response = await axios.get(`${API}/admin/me`, { headers: getAuthHeaders() });
      setAdmin(response.data);
      setIsAuthenticated(true);
    } catch (error) {
      console.error("Auth check failed:", error);
      logout();
    }
  }, [token, getAuthHeaders]);

  // Login
  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/admin/login`, { email, password });
      const { token: newToken, email: adminEmail, name } = response.data;
      localStorage.setItem("adminToken", newToken);
      setToken(newToken);
      setAdmin({ email: adminEmail, name });
      setIsAuthenticated(true);
      return { success: true };
    } catch (error) {
      console.error("Login failed:", error);
      return { success: false, error: error.response?.data?.detail || "Login failed" };
    }
  };

  // Logout
  const logout = () => {
    localStorage.removeItem("adminToken");
    setToken(null);
    setAdmin(null);
    setIsAuthenticated(false);
  };

  // API helpers with auth
  const apiGet = async (endpoint) => {
    const response = await axios.get(`${API}${endpoint}`, { headers: getAuthHeaders() });
    return response.data;
  };

  const apiPost = async (endpoint, data) => {
    const response = await axios.post(`${API}${endpoint}`, data, { headers: getAuthHeaders() });
    return response.data;
  };

  const apiPut = async (endpoint, data) => {
    const response = await axios.put(`${API}${endpoint}`, data, { headers: getAuthHeaders() });
    return response.data;
  };

  const apiDelete = async (endpoint) => {
    const response = await axios.delete(`${API}${endpoint}`, { headers: getAuthHeaders() });
    return response.data;
  };

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const value = {
    token,
    admin,
    isAuthenticated,
    login,
    logout,
    apiGet,
    apiPost,
    apiPut,
    apiDelete,
    getAuthHeaders,
    API,
  };

  return <AdminContext.Provider value={value}>{children}</AdminContext.Provider>;
};

export default AdminContext;
